import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Copy, Share2, Gift, ExternalLink } from "lucide-react";
import {
  createReferralLink,
  createLinkedInShareText,
} from "@/utils/referralValidation";
import { trackReferralEvent, ReferralEvents } from "@/utils/analytics";
import { useAuth } from "@/contexts/AuthContext";

interface ReferralSuccessModalProps {
  referralCode: string;
  onClose: () => void;
}

export function ReferralSuccessModal({
  referralCode,
  onClose,
}: ReferralSuccessModalProps) {
  const [isSharing, setIsSharing] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const referralLink = createReferralLink(referralCode);
  const linkedInText = createLinkedInShareText(referralLink);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast({
        title: "Link copiat!",
        description: "Link-ul de recomandare a fost copiat în clipboard.",
      });

      trackReferralEvent({
        event: ReferralEvents.LINK_COPIED,
        userId: user?.id,
        referralCode,
      });
    } catch (error) {
      toast({
        title: "Eroare",
        description: "Nu s-a putut copia link-ul. Încearcă din nou.",
        variant: "destructive",
      });
    }
  };

  const handleShareLinkedIn = () => {
    setIsSharing(true);

    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}&title=${encodeURIComponent("Raport NIS2 Completat cu Succes")}&summary=${encodeURIComponent(linkedInText)}`;

    // Open LinkedIn sharing window
    window.open(linkedInUrl, "linkedin-share", "width=600,height=600");

    trackReferralEvent({
      event: ReferralEvents.SHARED_LINKEDIN,
      userId: user?.id,
      referralCode,
    });

    setTimeout(() => setIsSharing(false), 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Gift className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Felicitări!</CardTitle>
          <p className="text-muted-foreground">
            Raportul tău NIS2 a fost generat cu succes. Primești acum acces la
            sistemul de recomandări!
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Referral Code Display */}
          <div className="bg-muted p-4 rounded-lg">
            <Label className="text-sm font-medium">
              Codul tău de recomandare:
            </Label>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 bg-background px-3 py-2 rounded border font-mono text-lg">
                {referralCode}
              </code>
              <Button size="sm" variant="outline" onClick={handleCopyLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-primary/5 p-4 rounded-lg">
            <h4 className="font-medium text-primary mb-2">
              Cum funcționează sistemul de recomandări?
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Invită alte companii să folosească platforma</li>
              <li>• Primești 1 credit pentru fiecare raport completat</li>
              <li>• 1 credit = 10% reducere (max 50%)</li>
              <li>• Folosește creditele pentru planuri premium</li>
            </ul>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={handleShareLinkedIn}
              disabled={isSharing}
              className="w-full"
              size="lg"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {isSharing ? "Se distribuie..." : "Distribuie pe LinkedIn"}
            </Button>

            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="w-full"
              size="lg"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiază link-ul de recomandare
            </Button>
          </div>

          {/* Referral Link Preview */}
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded">
            <div className="flex items-center gap-1 mb-1">
              <ExternalLink className="w-3 h-3" />
              Link de recomandare:
            </div>
            <div className="font-mono break-all">{referralLink}</div>
          </div>

          <Button onClick={onClose} variant="secondary" className="w-full">
            Închide
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
